import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
} from "@fluentui/react-components";
import Dropzone from "react-dropzone";
import apiServices from "../../../../service";
import FormikInputRequest from "../../../../service/hook/useFormik";
import ASCButton from "../../../../components/Button";
import UploadIcon from "../../../../assets/uploadicon.svg";
import DynamicDropzone from "../../../../components/DynamicDropzone/Dropzone";
import { getUserId } from "../../../../utils";
import TextEditor from "../../../../components/TextEditor";
import "../style.css";

interface DocumentActionsProps {
  currentPath: string[];
  basePath: string;
  activeCustomerId: string;
  notify: (msg: string, sub: string, type: string) => void;
  onRefresh: () => void;
}

type DialogAction = "addFolder" | "addDoc" | "";

const DocumentActions: React.FC<DocumentActionsProps> = ({
  currentPath,
  basePath,
  activeCustomerId,
  notify,
  onRefresh,
}) => {
  const [dialogAction, setDialogAction] = useState<DialogAction>("");

  return (
    <>
      <div className="doc-actions-bar">
        <ASCButton
          shape="rounded"
          // appearance="primary"
          // className="asc-button-primary"
          onClick={() => setDialogAction("addFolder")}
          label="Add Folder"
        />
        <ASCButton
          shape="rounded"
          appearance="primary"
          className="asc-button-primary"
          onClick={() => setDialogAction("addDoc")}
          label="Add Document"
        />
      </div>

      <AddFolderDialog
        isOpen={dialogAction === "addFolder"}
        onClose={() => setDialogAction("")}
        currentPath={currentPath}
        basePath={basePath}
        activeCustomerId={activeCustomerId}
        notify={notify}
        onRefresh={onRefresh}
      />

      <AddDocumentDialog
        isOpen={dialogAction === "addDoc"}
        onClose={() => setDialogAction("")}
        currentPath={currentPath}
        basePath={basePath}
        activeCustomerId={activeCustomerId}
        notify={notify}
        onRefresh={onRefresh}
      />
    </>
  );
};

// ─── Add Folder Dialog ───────────────────────────────────────────────────────

export interface AddFolderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentPath: string[];
  basePath: string;
  activeCustomerId: string;
  notify: (msg: string, sub: string, type: string) => void;
  onRefresh: () => void;
}

export function AddFolderDialog({
  isOpen,
  onClose,
  currentPath,
  basePath,
  activeCustomerId,
  notify,
  onRefresh,
}: AddFolderDialogProps) {
  const pathStr = currentPath.join("/");
  const [apiProcessing, setApiProcessing] = useState(false);
  const formik = FormikInputRequest({
    initialValues: { folderName: "" },
    onSubmitFn: async (values: any) => {
      setApiProcessing(true);
      const folderPath = pathStr ? `${basePath}/${pathStr}/` : `${basePath}/`;
      const folderNameWithPath = `${folderPath}${values.folderName}/`;
      try {
        const res = await apiServices.clientDocument.addFolder({
          folderName: folderNameWithPath,
          customer_id: activeCustomerId,
          visible: "public",
        });
        setTimeout(() => {
          notify(res.data.message, "", "success");
          onRefresh();
        }, 1000);
        setApiProcessing(false);
        onClose();
        formik.resetForm();
      } catch (error) {
        console.error("Error adding folder:", error);
      }
    },
    validate: (values: any) => {
      const errors: any = {};
      if (!values.folderName) {
        errors.folderName = "Folder Name is required";
      } else if (values.folderName.length > 256) {
        errors.folderName = "Folder Name cannot be longer than 256 characters";
      }
      return errors;
    },
  });
  useEffect(() => {
    if (isOpen) {
      formik.validateForm();
    }
  }, [isOpen]);

  const handleCancel = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={isOpen}>
      <DialogSurface className="dialog-surface">
        <DialogTitle>Add Folder</DialogTitle>
        <DialogContent>
          <div className="folder_doc">
            <Field
              label={<span>Folder Name{formik.errors.folderName && <span className="required-item">*</span>}</span>}
              validationState={formik.errors.folderName ? "error" : "none"}
              validationMessage={`${formik.errors.folderName || ""}`}
            >
              <Input
                appearance="outline"
                name="folderName"
                value={formik.values.folderName}
                onChange={(e) =>
                  formik.setFieldValue("folderName", e.target.value)
                }
              />
            </Field>
          </div>
        </DialogContent>
        <DialogActions className="flex-end">
          <ASCButton shape="rounded" onClick={handleCancel} label="Cancel" />
          <ASCButton
            shape="rounded"
            appearance="primary"
            onClick={() => formik.handleSubmit()}
            style={{ background: "#0f6cbd" }}
            disabled={Object.keys(formik.errors).length > 0 || apiProcessing}
            label={apiProcessing ? "Saving..." : "Save"}
          />
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
}

// ─── Add Document Dialog ─────────────────────────────────────────────────────

export interface AddDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentPath: string[];
  basePath: string;
  activeCustomerId: string;
  notify: (msg: string, sub: string, type: string) => void;
  onRefresh: () => void;
}

export function AddDocumentDialog({
  isOpen,
  onClose,
  currentPath,
  basePath,
  activeCustomerId,
  notify,
  onRefresh,
}: AddDocumentDialogProps) {
  const pathStr = currentPath.join("/");
  const userId = getUserId();
  const [rawFiles, setRawFiles] = useState<File[]>([]);
  const [scannedFiles, setScannedFiles] = useState<any[]>([]);
  const [fileSizeError, setFileSizeError] = useState<string>("");
  const [editorKey, setEditorKey] = useState(0);

  const isScanning = scannedFiles.some(
    (f) => f.status === "uploading" || f.status === "scanning",
  );

  const filesReady = scannedFiles.filter((f) => f.status === "success")
  const hasFile = rawFiles.length > 0;

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const formik = FormikInputRequest({
    initialValues: {
      file_name: "",
      description: "",
      file: "",
    },
    onSubmitFn: async (values: any) => {
      const scannedObjectIds = scannedFiles
        .filter((f) => f.objectId)
        .map((f) => f.objectId)
        .join(",");

      const formData = new FormData();
      formData.append("file_name", values.file_name);
      formData.append("folder_name", `${basePath}/${pathStr}`);
      formData.append("description", values.description);
      // formData.append("file", values.file);
      formData.append("file", "");
      formData.append("customer_id", activeCustomerId);
      formData.append("visible", "public");
      if (scannedObjectIds) {
        formData.append("objectIds", scannedObjectIds);
      }
      try {
        const res = await apiServices.clientDocument.addDocument(formData);
        setTimeout(() => {
          onRefresh();
          notify(res.data.message, "", "success");
        }, 1000);
        handleCancel();
      } catch (error) {
        console.error("Error adding document:", error);
      }
    },
    validate: (values: any) => {
      const errors: any = {};
      if (!values.file_name) {
        errors.file_name = "File Name is required";
      } else if (values.file_name.length > 256) {
        errors.file_name = "File Name cannot be longer than 256 characters";
      }
      if (values.description && values.description.length > 5000) {
        errors.description = "Description cannot be longer than 5000 characters";
      }
      const hasFailed = scannedFiles.some(
        (f) => f.status === "failed" || f.status === "error",
      );
      if (fileSizeError) {
        errors.file = fileSizeError;
      } else if (hasFailed) {
        errors.file = "File scan failed, please try again";
      } else if (rawFiles.length === 0 && !values.file) {
        errors.file = "File is required";
      }
      return errors;
    },
  });

  useEffect(() => {
    if (isOpen) {
      formik.validateForm();
    }
  }, [isOpen]);

  // Re-validate whenever file selection state changes so the error
  // updates immediately (rawFiles/scannedFiles/fileSizeError are not Formik values)
  useEffect(() => {
    formik.validateForm();
  }, [rawFiles, scannedFiles, fileSizeError]);

  const handleCancel = () => {
    onClose();
    formik.resetForm();
    setRawFiles([]);
    setScannedFiles([]);
    setFileSizeError("");
    setEditorKey((k) => k + 1);
  };

  return (
    <Dialog open={isOpen}>
      <DialogSurface className="dialog-surface">
        <DialogTitle>Add Document</DialogTitle>
        <DialogContent style={{ overflowY: "auto", maxHeight: "65vh", scrollbarWidth: "thin", scrollbarColor: "#20539380 #f1f1f1" }}>
          <div className="folder_doc">
            <Field
              label={
                <span>
                  Upload File<span className="required-item">*</span>
                </span>
              }
              validationState={formik.errors.file ? "error" : "none"}
              validationMessage={`${formik.errors.file || ""}`}
            >
              <div className="flex-block-doc">
                {!hasFile && filesReady.length === 0 && (
                  <Dropzone
                    multiple={false}
                    disabled={isScanning}
                    onDrop={(acceptedFiles: File[]) => {
                      if (acceptedFiles.length > 0) {
                        const file = acceptedFiles[0];
                        if (file.size > MAX_FILE_SIZE) {
                          setFileSizeError("File size must be less than 10MB");
                          return;
                        }
                        setFileSizeError("");
                        const renamedName = file.name.replace(/\s+/g, "_");
                        const renamedFile = new File([file], renamedName, { type: file.type }) as any;
                        const src = file as any;
                        if (src.path) renamedFile.path = src.path.replace(/\s+/g, "_");
                        if (src.relativePath) renamedFile.relativePath = src.relativePath.replace(/\s+/g, "_");
                        setRawFiles([renamedFile]);
                        formik.setFieldValue("file_name", file.name.replace(/\.[^/.]+$/, ""), false);
                      }
                    }}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <section className="container">
                        <div
                          {...getRootProps()}
                          className="file-drop"
                          style={{
                            textAlign: "center",
                            opacity: isScanning ? 0.4 : 1,
                            pointerEvents: isScanning ? "none" : "auto",
                            cursor: isScanning ? "not-allowed" : "pointer",
                          }}
                        >
                          <input {...getInputProps()} />
                          <img src={UploadIcon} width="50px" alt="upload" />
                          <p style={{ textAlign: "center" }}>
                            {isScanning
                              ? "File is being processed..."
                              : "Click or Drag the file here to upload"}
                          </p>
                        </div>
                      </section>
                    )}
                  </Dropzone>
                )}
              </div>

              {hasFile && (
                <DynamicDropzone
                  style={{ marginTop: "8px" }}
                  multiple={false}
                  externalFiles={rawFiles}
                  onFilesChange={(files: any) => {
                    setScannedFiles(files);
                    const successFile = files.find((f: any) => f.status === "success");
                    if (successFile) {
                      formik.setFieldValue("file", successFile.file, false);
                    } else if (files.length === 0) {
                      formik.setFieldValue("file", "", false);
                      setRawFiles([]);
                    }
                  }}
                  onFileRemoved={() => {
                    setRawFiles([]);
                    setScannedFiles([]);
                    formik.setFieldValue("file", "", false);
                  }}
                  userId={userId}
                />
              )}
            </Field>
          </div>

          <div className="folder_doc">
            <Field
              label={
                <span>
                  File Name<span className="required-item">*</span>
                </span>
              }
              validationState={formik.errors.file_name ? "error" : "none"}
              validationMessage={`${formik.errors.file_name || ""}`}
            >
              <Input
                appearance="outline"
                name="file_name"
                value={formik.values.file_name}
                onChange={(e) =>
                  formik.setFieldValue("file_name", e.target.value)
                }
              />
            </Field>
          </div>

          <div className="folder_doc">
            <Field label="Folder Name">
              <Input appearance="outline" disabled value={pathStr} />
            </Field>
          </div>

          <div className="folder_doc">
            <Field
              label={
                <span>
                  Description
                  {formik.errors.description && (
                    <span className="required-item">*</span>
                  )}
                </span>
              }
              validationState={formik.errors.description ? "error" : "none"}
              validationMessage={`${formik.errors.description || ""}`}
            >
              <TextEditor
                key={editorKey}
                inputData={formik.values.description}
                onChange={(value: string) => formik.setFieldValue("description", value)}
                disabled={false}
                name="description"
              />
            </Field>
          </div>
        </DialogContent>
        <DialogActions className="flex-end">
          <ASCButton shape="rounded" onClick={handleCancel} label="Cancel" />
          <ASCButton
            shape="rounded"
            appearance="primary"
            onClick={() => formik.handleSubmit()}
            style={{ background: "#0f6cbd" }}
            disabled={
              isScanning ||
              (rawFiles.length > 0 && scannedFiles.length === 0) ||
              Object.keys(formik.errors).length > 0
            }
            label={
              isScanning || (rawFiles.length > 0 && scannedFiles.length === 0)
                ? "Scanning..."
                : "Save"
            }
          />
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
}

export default DocumentActions;
